import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Disc3, Search, Loader2, Crown } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { discogsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import SearchResults from './SearchResults';

export default function MatrixSearch({ onSuccess }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [matrix, setMatrix] = useState('');
  const [artist, setArtist] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  
  const isPro = user?.plan === 'PRO' || user?.plan === 'LIFETIME_PRO';

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!matrix.trim()) {
      toast.error('Enter a matrix code');
      return;
    }

    setLoading(true);
    setResults(null);

    try {
      // Use the PRO matrix-search endpoint
      const res = await discogsAPI.matrixSearch(matrix.trim(), artist.trim() || null);
      const searchResults = res.data.results || [];
      
      if (searchResults.length === 0) {
        toast.info('No results found. Try adding the artist name.');
      } else {
        toast.success(`Found ${searchResults.length} results`);
      }

      setResults(searchResults);
    } catch (err) {
      console.error('Matrix search error:', err);
      if (err.response?.status === 403) {
        toast.error('Matrix search requires PRO subscription');
      } else {
        toast.error('Search failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setResults(null);
  };

  if (results) {
    return (
      <SearchResults
        results={results}
        onSelect={onSuccess}
        onBack={handleBack}
      />
    );
  }

  // Show upgrade prompt for FREE users
  if (!isPro) {
    return (
      <div className="space-y-6">
        <div className="text-center space-y-3">
          <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
            <Disc3 className="w-8 h-8 text-primary" />
          </div>
          <h3 className="font-medium">Search by pressing</h3>
          <p className="text-sm text-muted-foreground">
            Find the exact pressing of your vinyl by searching the matrix code
          </p>
        </div>

        <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg space-y-3">
          <div className="flex items-center gap-2 text-primary">
            <Crown className="w-4 h-4" />
            <span className="text-sm font-medium">PRO feature</span>
          </div>
          <p className="text-sm text-muted-foreground">
            Matrix search allows you for at identify the exact pressing af din vinyl 
            by searching the engraved code in the runout area. This is particularly useful for 
            samlere who want to find original editions or specific pressings.
          </p>
        </div>

        <div className="space-y-3">
          <div className="p-3 bg-secondary rounded-lg">
            <label className="text-sm font-medium mb-2 block text-muted-foreground">Matrix / Runout code</label>
            <Input
              placeholder="f.eks. A-1 MASTERDISK RL"
              disabled
              className="bg-background border-border font-mono opacity-50"
            />
          </div>
          <div className="p-3 bg-secondary rounded-lg">
            <label className="text-sm font-medium mb-2 block text-muted-foreground">Artist</label>
            <Input
              placeholder="f.eks. Dire Straits"
              disabled
              className="bg-background border-border opacity-50"
            />
          </div>
        </div>

        <Button
          onClick={() => navigate('/upgrade')}
          className="w-full gap-2"
        >
          <Crown className="w-4 h-4" />
          Upgrade to PRO
        </Button>

        <p className="text-xs text-center text-muted-foreground">
          PRO also includes batch import and collection export.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
          <Disc3 className="w-8 h-8 text-primary" />
        </div>
        <h3 className="font-medium">Search by pressing</h3>
        <p className="text-sm text-muted-foreground">
          Enter the matrix code from the vinyl runout to find the exact pressing
        </p>
      </div>

      <form onSubmit={handleSearch} className="space-y-4">
        <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg space-y-3">
          <div>
            <label className="text-sm font-medium mb-2 block">Matrix / Runout code *</label>
            <Input
              placeholder="f.eks. A-1 MASTERDISK RL eller 6359 034-A1"
              value={matrix}
              onChange={(e) => setMatrix(e.target.value)}
              className="bg-background border-border font-mono"
              data-testid="matrix-input"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            The matrix code is engraved on the vinyl runout (the blank area near the label)
          </p>
        </div>

        <div>
          <label className="text-sm font-medium mb-2 block">Artist (optional)</label>
          <Input
            placeholder="f.eks. Dire Straits"
            value={artist}
            onChange={(e) => setArtist(e.target.value)}
            className="bg-secondary border-border"
            data-testid="matrix-artist-input"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Add artist name for more accurate results
          </p>
        </div>

        <Button
          type="submit"
          disabled={loading || !matrix.trim()}
          className="w-full gap-2"
          data-testid="matrix-search-btn"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Search className="w-4 h-4" />
          )}
          Search by pressing
        </Button>
      </form>

      <div className="text-xs text-muted-foreground text-center space-y-1">
        <p className="font-medium">Tip: How to find the matrix code</p>
        <p>Look at the blank area between the last groove and the label. The code is engraved or stamped here.</p>
      </div>
    </div>
  );
}
